import { watch, onUnmounted, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { useCompanyStore } from '@cplug/frontend-shared-domains/domain/company'
import type { Company as BaseCompany } from '@cplug/frontend-shared-domains/domain/company'
import { useBrandStore } from '~/domain/shared/branding/stores/useBrandStore'

interface Company extends BaseCompany {
  schema?: string
}

function resolveCompanySchema (company: Company): string {
  return company.contract?.schema ?? company.schema ?? ''
}

declare global {
  interface Window {
    __PulsePesquisaCarregado?: boolean
    PulsePesquisa?: {
      abrir: () => void
      fechar: (permanente?: boolean) => void
      slug?: string
    }
  }
}

export interface UseCplugPulseOptions {
  widgetId: string
  erpVersion?: string
  requireTourCompleted?: boolean
  /** Limpa flags do widget Pulse no localStorage antes de injetar (ex.: pesquisa NPS). */
  clearDismissedState?: boolean
  /** Quando false, remove o script; quando true, injeta (ou reinjeta com clearDismissedState). */
  enabled?: Ref<boolean>
}

const PULSE_SCRIPT_ID = 'cplug-pulse-widget'
const PULSE_STYLE_ID = 'cplug-pulse-zindex-override'
const PULSE_SCRIPT_SRC = 'https://cplug-pulse.web.app/widget.js'
const TOUR_COMPLETED_KEY = 'navigation_tour_completed'
const PULSE_VISTO_PREFIX = '__pulse_visto_'
const PULSE_DEDUP_PREFIX = '__pulse_dedup_'
const PULSE_FORM_PREFIX = 'pulse_form_'

const DEFAULT_PULSE_OPTIONS: UseCplugPulseOptions = {
  widgetId: 'cplug-erp-v1-2026-05-02',
  erpVersion: 'erp-v1',
  requireTourCompleted: true
}

function isTourCompleted (): boolean {
  return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true'
}

/** Remove kill-switch e cache de dedup do widget Pulse para permitir reabertura. */
export function clearPulseDismissedState (widgetId: string): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  const widgetSegment = `_${widgetId}_`
  const keysToRemove: string[] = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key == null) {
      continue
    }

    if (key.startsWith(PULSE_VISTO_PREFIX) && key.includes(widgetSegment)) {
      keysToRemove.push(key)
      continue
    }

    if (key.startsWith(PULSE_DEDUP_PREFIX)) {
      keysToRemove.push(key)
      continue
    }

    if (key.startsWith(PULSE_FORM_PREFIX)) {
      keysToRemove.push(key)
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key))
}

let injected = false
let scriptOwnerId: symbol | null = null

function injectZIndexOverride (): void {
  if (document.getElementById(PULSE_STYLE_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = PULSE_STYLE_ID
  style.textContent = [
    '.__pulse-backdrop { z-index: 1050 !important; }',
    '.__pulse-fab { z-index: 1040 !important; }',
    '.__pulse-sheet { z-index: 1040 !important; }'
  ].join('\n')
  document.head.appendChild(style)
}

interface PulseScriptMeta {
  companyId: number
  companyName: string
  schema: string
  userId: number
  erpVersion: string
}

function removePulseScript (requesterId?: symbol, force = false): void {
  if (!force && requesterId != null && scriptOwnerId !== requesterId) {
    return
  }

  const script = document.getElementById(PULSE_SCRIPT_ID)
  if (script) {
    script.remove()
  }

  const style = document.getElementById(PULSE_STYLE_ID)
  if (style) {
    style.remove()
  }

  document.querySelectorAll('[class*="__pulse-"]').forEach(el => el.remove())

  if (window.__PulsePesquisaCarregado) {
    delete window.__PulsePesquisaCarregado
  }
  if (window.PulsePesquisa) {
    delete window.PulsePesquisa
  }

  injected = false
  scriptOwnerId = null
}

function injectPulseScript (
  meta: PulseScriptMeta,
  widgetId: string,
  ownerId: symbol,
  clearDismissedState = false
): void {
  const existingScript = document.getElementById(PULSE_SCRIPT_ID) as HTMLScriptElement | null
  const isSameActiveScript = injected &&
    existingScript != null &&
    existingScript.dataset.widget === widgetId &&
    scriptOwnerId === ownerId

  if (isSameActiveScript) {
    return
  }

  if (clearDismissedState) {
    clearPulseDismissedState(widgetId)
  }

  removePulseScript(undefined, true)

  injectZIndexOverride()

  const script = document.createElement('script')
  script.id = PULSE_SCRIPT_ID
  script.async = true
  script.src = PULSE_SCRIPT_SRC
  script.dataset.widget = widgetId
  script.dataset.metaCompany_id = String(meta.companyId)
  script.dataset.metaCompany_name = meta.companyName
  script.dataset.metaSchema = meta.schema
  script.dataset.metaUser_id = String(meta.userId)
  script.dataset.metaErp_version = meta.erpVersion

  document.body.appendChild(script)
  injected = true
  scriptOwnerId = ownerId
}

function shouldSkipTourGate (requireTourCompleted: boolean): boolean {
  return requireTourCompleted && !isTourCompleted()
}

/**
 * Injects the Cplug Pulse feedback widget.
 * Must be called inside a component's setup — the script is removed on unmount
 * so it only lives while the host component (e.g. HomeDashboard) is mounted.
 *
 * Z-index is overridden to sit below PrimeVue overlays (modal: 1100, overlay: 1200)
 * so Pulse never covers dialogs, menus or tooltips.
 */
export function useCplugPulse (options?: UseCplugPulseOptions): void {
  const pulseOptions: UseCplugPulseOptions = {
    ...DEFAULT_PULSE_OPTIONS,
    ...options
  }
  const ownerId = Symbol('cplug-pulse-consumer')

  const authStore = useAuthenticationStore()
  const companyStore = useCompanyStore()
  const { logged, companies, user } = storeToRefs(authStore)
  const { isPartnerBrand } = storeToRefs(useBrandStore())
  const { pageList: companiesList } = storeToRefs(companyStore)

  function isPulseEnabled (): boolean {
    return pulseOptions.enabled?.value ?? true
  }

  function tryInject (): void {
    if (!isPulseEnabled()) {
      removePulseScript(ownerId)
      return
    }

    const isLogged = logged.value
    const currentCompany = (companies.value as Company[])?.[0]
    const currentUser = user?.value
    const findedCompany = (companiesList.value as Company[] | undefined)?.find(c => c.id === currentCompany?.id)
    const company = findedCompany ?? currentCompany

    if (!isLogged || !company?.id || !currentUser?.id || isPartnerBrand.value || shouldSkipTourGate(pulseOptions.requireTourCompleted ?? true)) {
      removePulseScript(ownerId)
      return
    }

    injectPulseScript({
      companyId: company.id,
      companyName: company.name,
      schema: resolveCompanySchema(company),
      userId: currentUser.id,
      erpVersion: pulseOptions.erpVersion ?? DEFAULT_PULSE_OPTIONS.erpVersion!
    }, pulseOptions.widgetId, ownerId, pulseOptions.clearDismissedState ?? false)
  }

  watch(
    () => ({
      enabled: isPulseEnabled(),
      isLogged: logged.value,
      companies: companies.value as Company[],
      currentUser: user?.value,
      partnerBrand: isPartnerBrand.value
    }),
    ({ enabled, isLogged, companies: selectedCompanies, currentUser, partnerBrand }) => {
      if (!enabled) {
        removePulseScript(ownerId)
        return
      }

      const currentCompany = selectedCompanies?.[0]
      const findedCompany = (companiesList.value as Company[] | undefined)?.find(c => c.id === currentCompany?.id)
      const company = findedCompany ?? currentCompany

      if (!isLogged || !company?.id || !currentUser?.id || partnerBrand) {
        removePulseScript(ownerId)
        return
      }

      if (shouldSkipTourGate(pulseOptions.requireTourCompleted ?? true)) {
        removePulseScript(ownerId)
        return
      }

      injectPulseScript({
        companyId: company.id,
        companyName: company.name,
        schema: resolveCompanySchema(company),
        userId: currentUser.id,
        erpVersion: pulseOptions.erpVersion ?? DEFAULT_PULSE_OPTIONS.erpVersion!
      }, pulseOptions.widgetId, ownerId, pulseOptions.clearDismissedState ?? false)
    },
    { immediate: true, deep: true }
  )

  function onStorageChange (event: StorageEvent): void {
    if (event.key === TOUR_COMPLETED_KEY && event.newValue === 'true') {
      tryInject()
    }
  }

  window.addEventListener('storage', onStorageChange)

  onUnmounted(() => {
    window.removeEventListener('storage', onStorageChange)
    removePulseScript(ownerId)
  })
}
